/**
 * UwFallbackDialog - the E7 fallback after a rejection: a new Antrag beim gleichen Versicherer, or
 * abort the Ausschreibung (10.1; broker only, typed confirm) (P2 Slice 14 D4).
 */

import { useEffect, useState } from 'react';
import { Dialog } from '../administration/components';
import { Button } from '../../components/ui/Button';
import { UW_ABBRUCH_BESTAETIGUNG, type UwFallbackBody } from '@brokly/shared';

const inputCls = 'w-full rounded-md border bg-card px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

const ERROR_TEXT: Record<string, string> = {
  grund_required: 'Bitte einen Grund angeben',
  fallback_bereits_erfasst: 'Es wurde bereits ein weiteres Vorgehen erfasst',
  antrag_status_invalid: 'Der Antrag ist nicht mehr in diesem Status',
  invalid_transition: 'Die Ausschreibung kann nicht abgebrochen werden',
};

const CONFIRM_WORD = 'ABBRECHEN';

export function UwFallbackDialog({
  open,
  onClose,
  onSubmit,
  saving,
  error,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (body: UwFallbackBody) => void;
  saving: boolean;
  error: string | null;
}) {
  const [art, setArt] = useState<'gleicher_versicherer' | 'abbruch'>('gleicher_versicherer');
  const [grund, setGrund] = useState('');
  const [confirm, setConfirm] = useState('');

  useEffect(() => {
    if (open) {
      setArt('gleicher_versicherer');
      setGrund('');
      setConfirm('');
    }
  }, [open]);

  const confirmOk = art === 'gleicher_versicherer' || confirm.trim() === CONFIRM_WORD;
  const canSubmit = grund.trim().length >= 3 && confirmOk;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Weiteres Vorgehen"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>Zurueck</Button>
          <Button variant={art === 'abbruch' ? 'destructive' : 'default'} onClick={() => onSubmit({ art, grund: grund.trim() })} disabled={saving || !canSubmit} data-testid="uw-fallback-submit">
            Erfassen
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3" data-testid="uw-fallback-dialog">
        <fieldset className="text-sm">
          <legend className="text-muted-foreground">Vorgehen</legend>
          <label className="block"><input type="radio" name="uw-fallback-art" checked={art === 'gleicher_versicherer'} onChange={() => setArt('gleicher_versicherer')} data-testid="uw-fallback-gleicher" /> Neuer Antrag beim gleichen Versicherer</label>
          <label className="block"><input type="radio" name="uw-fallback-art" checked={art === 'abbruch'} onChange={() => setArt('abbruch')} data-testid="uw-fallback-abbruch" /> Ausschreibung abbrechen</label>
        </fieldset>
        <label className="text-sm">
          Grund
          <textarea value={grund} onChange={(e) => setGrund(e.target.value)} rows={3} className={inputCls} data-testid="uw-fallback-grund" />
        </label>
        {art === 'abbruch' && (
          <div className="rounded-md border border-status-danger/40 p-3 text-sm">
            <p className="text-status-danger">{UW_ABBRUCH_BESTAETIGUNG}</p>
            <label className="mt-2 block">
              Zum Bestaetigen {CONFIRM_WORD} eingeben
              <input value={confirm} onChange={(e) => setConfirm(e.target.value)} className={inputCls} data-testid="uw-fallback-confirm" />
            </label>
          </div>
        )}
        {error && <p role="alert" className="text-sm text-status-danger" data-testid="uw-fallback-error">{ERROR_TEXT[error] ?? error}</p>}
      </div>
    </Dialog>
  );
}
